import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { FaSave, FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import { getPermissions, updateRole, clearState } from "../redux/Slices/authSlice";
import { useTitle } from "../context/TitleContext";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import { translatePermission } from "../Utils/permissionLabels";
import Pagination from '../components/Pagination';

const EditRole = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { permissions, permissionsPagination, isLoading: loading, error, success } =
    useSelector((state) => state.auth);

  const [roleData, setRoleData] = useState({
    name: "",
    permission_id: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [originalRole, setOriginalRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    setTitle("تعديل الصلاحية");
    dispatch(clearState());
    dispatch(getPermissions(currentPage));

    // جلب بيانات الصلاحية المخزنة محلياً
    const roleDataFromStorage = sessionStorage.getItem(`role_${id}`);
    if (roleDataFromStorage) {
      try {
        const parsedRole = JSON.parse(roleDataFromStorage);
        setOriginalRole(parsedRole);
        setRoleData({
          name: parsedRole.name,
          permission_id: parsedRole.permissions || [],
        });
      } catch (err) {
        console.error("Error parsing role data:", err);
      }
    }
    setRoleLoading(false);
  }, [dispatch, setTitle, currentPage, id]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        position: "top-center",
        zIndex: 9999,
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      dispatch(clearState());
      setTimeout(() => {
        navigate('/roles');
      }, 1000);
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        zIndex: 9999,
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      dispatch(clearState());
    }
  }, [error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (permission) => {
    const id = typeof permission === "object" ? permission.id : permission;
    const permissionObj = permissions.find(p => p.id === id);
    
    if (!permissionObj) {
      return;
    }

    setRoleData((prev) => {
      const exists = prev.permission_id.some(
        (p) => (typeof p === "object" ? p.id : p) === id
      );

      if (exists) {
        // محاولة إزالة الصلاحية - التحقق من قواعد view permissions
        const canRemove = checkIfCanRemoveViewPermission(permissionObj.name, prev.permission_id);
        
        if (!canRemove.allowed) {
          toast.error(canRemove.message, {
            position: "top-center",
            zIndex: 9999,
            autoClose: 5000,
          });
          return prev;
        }

        return {
          ...prev,
          permission_id: prev.permission_id.filter(
            (p) => (typeof p === "object" ? p.id : p) !== id
          ),
        };
      } else {
        return {
          ...prev,
          permission_id: [...prev.permission_id, id],
        };
      }
    });
  };

  const checkIfCanRemoveViewPermission = (permissionName, currentPermissions) => {
    const selectedPermissionNames = permissions
      .filter(p => currentPermissions.includes(p.id))
      .map(p => p.name);
    
    const viewPermissionGroups = {
      'view-users': ['create-users', 'edit-users', 'delete-users'],
      'view-admins': ['create-admins', 'edit-admins', 'delete-admins'],
      'view-roles': ['create-roles', 'edit-roles', 'delete-roles'],
      'view-cities': ['create-cities', 'edit-cities', 'delete-cities'],
      'view-regions': ['create-regions', 'edit-regions', 'delete-regions'],
      'view-contacts': ['delete-contacts'],
      'view-faqs': ['create-faqs', 'edit-faqs', 'delete-faqs'],
      'view-categories': ['create-categories', 'edit-categories', 'delete-categories'],
      'view-features': ['create-features', 'edit-features', 'delete-features'],
      'view-grades': ['create-grades', 'edit-grades', 'delete-grades'],
      'view-messages': ['create-messages', 'edit-messages', 'delete-messages'],
      'view-products': ['create-products', 'edit-products', 'delete-products'],
      'view-sections': ['create-sections', 'edit-sections', 'delete-sections'],
      'view-sliders': ['create-sliders', 'edit-sliders', 'delete-sliders'],
      'view-slider-texts': ['create-slider-texts', 'edit-slider-texts', 'delete-slider-texts'],
      'view-subjects': ['create-subjects', 'edit-subjects', 'delete-subjects'],
      'view-teachers': ['create-teachers', 'edit-teachers', 'delete-teachers'],
      'view-orders': ['show-orders', 'delete-orders', 'change-status-orders', 'change-payment-status-orders']
    };

    if (!permissionName.startsWith('view-')) {
      return { allowed: true, message: '' };
    }

    const relatedPermissions = viewPermissionGroups[permissionName];
    if (!relatedPermissions) {
      return { allowed: true, message: '' };
    }

    const hasRelatedPermissions = relatedPermissions.some(perm => selectedPermissionNames.includes(perm));
    
    if (hasRelatedPermissions) {
      const relatedPermsFound = relatedPermissions.filter(perm => selectedPermissionNames.includes(perm));
      const permNames = {
        'view-users': 'المستخدمين',
        'view-admins': 'الأدمن',
        'view-roles': 'الصلاحيات',
        'view-cities': 'المدن',
        'view-regions': 'المناطق',
        'view-contacts': 'التواصل',
        'view-faqs': 'الأسئلة الشائعة',
        'view-categories': 'الفئات',
        'view-features': 'المميزات',
        'view-grades': 'الصفوف',
        'view-messages': 'الرسائل',
        'view-products': 'المنتجات',
        'view-sections': 'الأقسام',
        'view-sliders': 'السلايدر',
        'view-slider-texts': 'نصوص السلايدر',
        'view-subjects': 'المواد الدراسية',
        'view-teachers': 'المعلمين',
        'view-orders': 'الطلبات'
      };

      const moduleName = permNames[permissionName] || permissionName;
      
      return {
        allowed: false,
        message: `لا يمكن إزالة صلاحية عرض ${moduleName} لأن لديك صلاحيات ${relatedPermsFound.map(p => {
          const permNames = {
            'create-users': 'إنشاء المستخدمين',
            'edit-users': 'تعديل المستخدمين',
            'delete-users': 'حذف المستخدمين',
            'create-admins': 'إنشاء الأدمن',
            'edit-admins': 'تعديل الأدمن',
            'delete-admins': 'حذف الأدمن',
            'create-roles': 'إنشاء الصلاحيات',
            'edit-roles': 'تعديل الصلاحيات',
            'delete-roles': 'حذف الصلاحيات',
            'create-cities': 'إنشاء المدن',
            'edit-cities': 'تعديل المدن',
            'delete-cities': 'حذف المدن',
            'create-regions': 'إنشاء المناطق',
            'edit-regions': 'تعديل المناطق',
            'delete-regions': 'حذف المناطق',
            'delete-contacts': 'حذف التواصل',
            'create-faqs': 'إنشاء الأسئلة الشائعة',
            'edit-faqs': 'تعديل الأسئلة الشائعة',
            'delete-faqs': 'حذف الأسئلة الشائعة',
            'create-categories': 'إنشاء الفئات',
            'edit-categories': 'تعديل الفئات',
            'delete-categories': 'حذف الفئات',
            'create-features': 'إنشاء المميزات',
            'edit-features': 'تعديل المميزات',
            'delete-features': 'حذف المميزات',
            'create-grades': 'إنشاء الصفوف',
            'edit-grades': 'تعديل الصفوف',
            'delete-grades': 'حذف الصفوف',
            'create-messages': 'إنشاء الرسائل',
            'edit-messages': 'تعديل الرسائل',
            'delete-messages': 'حذف الرسائل',
            'create-products': 'إنشاء المنتجات',
            'edit-products': 'تعديل المنتجات',
            'delete-products': 'حذف المنتجات',
            'create-sections': 'إنشاء الأقسام',
            'edit-sections': 'تعديل الأقسام',
            'delete-sections': 'حذف الأقسام',
            'create-sliders': 'إنشاء السلايدر',
            'edit-sliders': 'تعديل السلايدر',
            'delete-sliders': 'حذف السلايدر',
            'create-slider-texts': 'إنشاء نصوص السلايدر',
            'edit-slider-texts': 'تعديل نصوص السلايدر',
            'delete-slider-texts': 'حذف نصوص السلايدر',
            'create-subjects': 'إنشاء المواد الدراسية',
            'edit-subjects': 'تعديل المواد الدراسية',
            'delete-subjects': 'حذف المواد الدراسية',
            'create-teachers': 'إنشاء المعلمين',
            'edit-teachers': 'تعديل المعلمين',
            'delete-teachers': 'حذف المعلمين',
            'show-orders': 'عرض الطلبات',
            'delete-orders': 'حذف الطلبات',
            'change-status-orders': 'تغيير حالة الطلبات',
            'change-payment-status-orders': 'تغيير حالة الدفع'
          };
          return permNames[p] || p;
        }).join('، ')}`
      };
    }

    return { allowed: true, message: '' };
  };

  const validatePermissions = () => {
    const selectedPermissionNames = permissions
      .filter(p => roleData.permission_id.includes(p.id))
      .map(p => p.name);
    
    const errors = [];

    // التحقق من الصلاحيات المتعلقة بالمستخدمين
    const hasCreateUsers = selectedPermissionNames.includes('create-users');
    const hasEditUsers = selectedPermissionNames.includes('edit-users');
    const hasDeleteUsers = selectedPermissionNames.includes('delete-users');
    const hasViewUsers = selectedPermissionNames.includes('view-users');
    
    if (hasCreateUsers && !hasViewUsers) {
      errors.push('يجب اختيار "view-users" عند اختيار "create-users"');
    }
    if (hasEditUsers && !hasViewUsers) {
      errors.push('يجب اختيار "view-users" عند اختيار "edit-users"');
    }
    if (hasDeleteUsers && !hasViewUsers) {
      errors.push('يجب اختيار "view-users" عند اختيار "delete-users"');
    }

    // التحقق من الصلاحيات المتعلقة بالأدمن
    const hasCreateAdmins = selectedPermissionNames.includes('create-admins');
    const hasEditAdmins = selectedPermissionNames.includes('edit-admins');
    const hasDeleteAdmins = selectedPermissionNames.includes('delete-admins');
    const hasViewAdmins = selectedPermissionNames.includes('view-admins');
    
    if (hasCreateAdmins && !hasViewAdmins) {
      errors.push('يجب اختيار "view-admins" عند اختيار "create-admins"');
    }
    if (hasEditAdmins && !hasViewAdmins) {
      errors.push('يجب اختيار "view-admins" عند اختيار "edit-admins"');
    }
    if (hasDeleteAdmins && !hasViewAdmins) {
      errors.push('يجب اختيار "view-admins" عند اختيار "delete-admins"');
    }

    // التحقق من الصلاحيات المتعلقة بالصلاحيات
    const hasCreateRoles = selectedPermissionNames.includes('create-roles');
    const hasEditRoles = selectedPermissionNames.includes('edit-roles');
    const hasDeleteRoles = selectedPermissionNames.includes('delete-roles');
    const hasViewRoles = selectedPermissionNames.includes('view-roles');
    
    if (hasCreateRoles && !hasViewRoles) {
      errors.push('يجب اختيار "view-roles" عند اختيار "create-roles"');
    }
    if (hasEditRoles && !hasViewRoles) {
      errors.push('يجب اختيار "view-roles" عند اختيار "edit-roles"');
    }
    if (hasDeleteRoles && !hasViewRoles) {
      errors.push('يجب اختيار "view-roles" عند اختيار "delete-roles"');
    }

    // التحقق من الصلاحيات المتعلقة بالمدن
    const hasCreateCities = selectedPermissionNames.includes('create-cities');
    const hasEditCities = selectedPermissionNames.includes('edit-cities');
    const hasDeleteCities = selectedPermissionNames.includes('delete-cities');
    const hasViewCities = selectedPermissionNames.includes('view-cities');
    
    if (hasCreateCities && !hasViewCities) {
      errors.push('يجب اختيار "view-cities" عند اختيار "create-cities"');
    }
    if (hasEditCities && !hasViewCities) {
      errors.push('يجب اختيار "view-cities" عند اختيار "edit-cities"');
    }
    if (hasDeleteCities && !hasViewCities) {
      errors.push('يجب اختيار "view-cities" عند اختيار "delete-cities"');
    }

    // التحقق من الصلاحيات المتعلقة بالمناطق
    const hasCreateRegions = selectedPermissionNames.includes('create-regions');
    const hasEditRegions = selectedPermissionNames.includes('edit-regions');
    const hasDeleteRegions = selectedPermissionNames.includes('delete-regions');
    const hasViewRegions = selectedPermissionNames.includes('view-regions');
    
    if (hasCreateRegions && !hasViewRegions) {
      errors.push('يجب اختيار "view-regions" عند اختيار "create-regions"');
    }
    if (hasEditRegions && !hasViewRegions) {
      errors.push('يجب اختيار "view-regions" عند اختيار "edit-regions"');
    }
    if (hasDeleteRegions && !hasViewRegions) {
      errors.push('يجب اختيار "view-regions" عند اختيار "delete-regions"');
    }

    // التحقق من الصلاحيات المتعلقة بالتواصل
    const hasDeleteContacts = selectedPermissionNames.includes('delete-contacts');
    const hasViewContacts = selectedPermissionNames.includes('view-contacts');
    
    if (hasDeleteContacts && !hasViewContacts) {
      errors.push('يجب اختيار "view-contacts" عند اختيار "delete-contacts"');
    }

    // التحقق من الصلاحيات المتعلقة بالأسئلة الشائعة
    const hasCreateFaqs = selectedPermissionNames.includes('create-faqs');
    const hasEditFaqs = selectedPermissionNames.includes('edit-faqs');
    const hasDeleteFaqs = selectedPermissionNames.includes('delete-faqs');
    const hasViewFaqs = selectedPermissionNames.includes('view-faqs');
    
    if (hasCreateFaqs && !hasViewFaqs) {
      errors.push('يجب اختيار "view-faqs" عند اختيار "create-faqs"');
    }
    if (hasEditFaqs && !hasViewFaqs) {
      errors.push('يجب اختيار "view-faqs" عند اختيار "edit-faqs"');
    }
    if (hasDeleteFaqs && !hasViewFaqs) {
      errors.push('يجب اختيار "view-faqs" عند اختيار "delete-faqs"');
    }

    // التحقق من الصلاحيات المتعلقة بالفئات
    const hasCreateCategories = selectedPermissionNames.includes('create-categories');
    const hasEditCategories = selectedPermissionNames.includes('edit-categories');
    const hasDeleteCategories = selectedPermissionNames.includes('delete-categories');
    const hasViewCategories = selectedPermissionNames.includes('view-categories');
    
    if (hasCreateCategories && !hasViewCategories) {
      errors.push('يجب اختيار "view-categories" عند اختيار "create-categories"');
    }
    if (hasEditCategories && !hasViewCategories) {
      errors.push('يجب اختيار "view-categories" عند اختيار "edit-categories"');
    }
    if (hasDeleteCategories && !hasViewCategories) {
      errors.push('يجب اختيار "view-categories" عند اختيار "delete-categories"');
    }

    // التحقق من الصلاحيات المتعلقة بالمميزات
    const hasCreateFeatures = selectedPermissionNames.includes('create-features');
    const hasEditFeatures = selectedPermissionNames.includes('edit-features');
    const hasDeleteFeatures = selectedPermissionNames.includes('delete-features');
    const hasViewFeatures = selectedPermissionNames.includes('view-features');
    
    if (hasCreateFeatures && !hasViewFeatures) {
      errors.push('يجب اختيار "view-features" عند اختيار "create-features"');
    }
    if (hasEditFeatures && !hasViewFeatures) {
      errors.push('يجب اختيار "view-features" عند اختيار "edit-features"');
    }
    if (hasDeleteFeatures && !hasViewFeatures) {
      errors.push('يجب اختيار "view-features" عند اختيار "delete-features"');
    }

    // التحقق من الصلاحيات المتعلقة بالصفوف
    const hasCreateGrades = selectedPermissionNames.includes('create-grades');
    const hasEditGrades = selectedPermissionNames.includes('edit-grades');
    const hasDeleteGrades = selectedPermissionNames.includes('delete-grades');
    const hasViewGrades = selectedPermissionNames.includes('view-grades');
    
    if (hasCreateGrades && !hasViewGrades) {
      errors.push('يجب اختيار "view-grades" عند اختيار "create-grades"');
    }
    if (hasEditGrades && !hasViewGrades) {
      errors.push('يجب اختيار "view-grades" عند اختيار "edit-grades"');
    }
    if (hasDeleteGrades && !hasViewGrades) {
      errors.push('يجب اختيار "view-grades" عند اختيار "delete-grades"');
    }

    // التحقق من الصلاحيات المتعلقة بالرسائل
    const hasCreateMessages = selectedPermissionNames.includes('create-messages');
    const hasEditMessages = selectedPermissionNames.includes('edit-messages');
    const hasDeleteMessages = selectedPermissionNames.includes('delete-messages');
    const hasViewMessages = selectedPermissionNames.includes('view-messages');
    
    if (hasCreateMessages && !hasViewMessages) {
      errors.push('يجب اختيار "view-messages" عند اختيار "create-messages"');
    }
    if (hasEditMessages && !hasViewMessages) {
      errors.push('يجب اختيار "view-messages" عند اختيار "edit-messages"');
    }
    if (hasDeleteMessages && !hasViewMessages) {
      errors.push('يجب اختيار "view-messages" عند اختيار "delete-messages"');
    }

    // التحقق من الصلاحيات المتعلقة بالمنتجات
    const hasCreateProducts = selectedPermissionNames.includes('create-products');
    const hasEditProducts = selectedPermissionNames.includes('edit-products');
    const hasDeleteProducts = selectedPermissionNames.includes('delete-products');
    const hasViewProducts = selectedPermissionNames.includes('view-products');
    
    if (hasCreateProducts && !hasViewProducts) {
      errors.push('يجب اختيار "view-products" عند اختيار "create-products"');
    }
    if (hasEditProducts && !hasViewProducts) {
      errors.push('يجب اختيار "view-products" عند اختيار "edit-products"');
    }
    if (hasDeleteProducts && !hasViewProducts) {
      errors.push('يجب اختيار "view-products" عند اختيار "delete-products"');
    }

    // التحقق من الصلاحيات المتعلقة بالأقسام
    const hasCreateSections = selectedPermissionNames.includes('create-sections');
    const hasEditSections = selectedPermissionNames.includes('edit-sections');
    const hasDeleteSections = selectedPermissionNames.includes('delete-sections');
    const hasViewSections = selectedPermissionNames.includes('view-sections');
    
    if (hasCreateSections && !hasViewSections) {
      errors.push('يجب اختيار "view-sections" عند اختيار "create-sections"');
    }
    if (hasEditSections && !hasViewSections) {
      errors.push('يجب اختيار "view-sections" عند اختيار "edit-sections"');
    }
    if (hasDeleteSections && !hasViewSections) {
      errors.push('يجب اختيار "view-sections" عند اختيار "delete-sections"');
    }

    // التحقق من الصلاحيات المتعلقة بالسلايدر
    const hasCreateSliders = selectedPermissionNames.includes('create-sliders');
    const hasEditSliders = selectedPermissionNames.includes('edit-sliders');
    const hasDeleteSliders = selectedPermissionNames.includes('delete-sliders');
    const hasViewSliders = selectedPermissionNames.includes('view-sliders');
    
    if (hasCreateSliders && !hasViewSliders) {
      errors.push('يجب اختيار "view-sliders" عند اختيار "create-sliders"');
    }
    if (hasEditSliders && !hasViewSliders) {
      errors.push('يجب اختيار "view-sliders" عند اختيار "edit-sliders"');
    }
    if (hasDeleteSliders && !hasViewSliders) {
      errors.push('يجب اختيار "view-sliders" عند اختيار "delete-sliders"');
    }

    // التحقق من الصلاحيات المتعلقة بنصوص السلايدر
    const hasCreateSliderTexts = selectedPermissionNames.includes('create-slider-texts');
    const hasEditSliderTexts = selectedPermissionNames.includes('edit-slider-texts');
    const hasDeleteSliderTexts = selectedPermissionNames.includes('delete-slider-texts');
    const hasViewSliderTexts = selectedPermissionNames.includes('view-slider-texts');
    
    if (hasCreateSliderTexts && !hasViewSliderTexts) {
      errors.push('يجب اختيار "view-slider-texts" عند اختيار "create-slider-texts"');
    }
    if (hasEditSliderTexts && !hasViewSliderTexts) {
      errors.push('يجب اختيار "view-slider-texts" عند اختيار "edit-slider-texts"');
    }
    if (hasDeleteSliderTexts && !hasViewSliderTexts) {
      errors.push('يجب اختيار "view-slider-texts" عند اختيار "delete-slider-texts"');
    }

    // التحقق من الصلاحيات المتعلقة بالمواد الدراسية
    const hasCreateSubjects = selectedPermissionNames.includes('create-subjects');
    const hasEditSubjects = selectedPermissionNames.includes('edit-subjects');
    const hasDeleteSubjects = selectedPermissionNames.includes('delete-subjects');
    const hasViewSubjects = selectedPermissionNames.includes('view-subjects');
    
    if (hasCreateSubjects && !hasViewSubjects) {
      errors.push('يجب اختيار "view-subjects" عند اختيار "create-subjects"');
    }
    if (hasEditSubjects && !hasViewSubjects) {
      errors.push('يجب اختيار "view-subjects" عند اختيار "edit-subjects"');
    }
    if (hasDeleteSubjects && !hasViewSubjects) {
      errors.push('يجب اختيار "view-subjects" عند اختيار "delete-subjects"');
    }

    // التحقق من الصلاحيات المتعلقة بالمعلمين
    const hasCreateTeachers = selectedPermissionNames.includes('create-teachers');
    const hasEditTeachers = selectedPermissionNames.includes('edit-teachers');
    const hasDeleteTeachers = selectedPermissionNames.includes('delete-teachers');
    const hasViewTeachers = selectedPermissionNames.includes('view-teachers');
    
    if (hasCreateTeachers && !hasViewTeachers) {
      errors.push('يجب اختيار "view-teachers" عند اختيار "create-teachers"');
    }
    if (hasEditTeachers && !hasViewTeachers) {
      errors.push('يجب اختيار "view-teachers" عند اختيار "edit-teachers"');
    }
    if (hasDeleteTeachers && !hasViewTeachers) {
      errors.push('يجب اختيار "view-teachers" عند اختيار "delete-teachers"');
    }

    // التحقق من الصلاحيات المتعلقة بالطلبات
    const hasShowOrders = selectedPermissionNames.includes('show-orders');
    const hasDeleteOrders = selectedPermissionNames.includes('delete-orders');
    const hasChangeStatusOrders = selectedPermissionNames.includes('change-status-orders');
    const hasChangePaymentStatusOrders = selectedPermissionNames.includes('change-payment-status-orders');
    const hasViewOrders = selectedPermissionNames.includes('view-orders');
    
    if (hasShowOrders && !hasViewOrders) {
      errors.push('يجب اختيار "view-orders" عند اختيار "show-orders"');
    }
    if (hasDeleteOrders && !hasViewOrders) {
      errors.push('يجب اختيار "view-orders" عند اختيار "delete-orders"');
    }
    if (hasChangeStatusOrders && !hasViewOrders) {
      errors.push('يجب اختيار "view-orders" عند اختيار "change-status-orders"');
    }
    if (hasChangePaymentStatusOrders && !hasViewOrders) {
      errors.push('يجب اختيار "view-orders" عند اختيار "change-payment-status-orders"');
    }

    return errors;
  };

  const handleSelectAll = () => {
    const allPermissionIds = permissions.map((p) => p.id);
    setRoleData((prev) => ({
      ...prev,
      permission_id:
        prev.permission_id.length === allPermissionIds.length ? [] : allPermissionIds,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearState());

    if (!roleData.name.trim()) {
      toast.error("يرجى إدخال اسم الصلاحية", {
        position: "top-center",
        zIndex: 9999,
      });
      return;
    }

    if (roleData.permission_id.length === 0) {
      toast.error("يرجى اختيار صلاحية واحدة على الأقل", {
        position: "top-center",
        zIndex: 9999,
      });
      return;
    }

    // التحقق من صحة اختيار الصلاحيات
    const validationErrors = validatePermissions();
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => {
        toast.error(error, {
          position: "top-center",
          zIndex: 9999,
          autoClose: 5000,
        });
      });
      return;
    }

    // تجهيز الداتا للفورم
    const formData = new FormData();
    formData.append("name", roleData.name);

    roleData.permission_id.forEach((permissionId, index) => {
      const value =
        typeof permissionId === "object" && permissionId !== null
          ? permissionId.id
          : permissionId;

      formData.append(`permissions[${index}]`, value);
    });

    dispatch(updateRole({ roleId: id, roleData: formData }));
  };

  const handlePageChange = (page) => {
    if (loading || page === currentPage) return;
    setCurrentPage(page);
  };

  const LoadingSpinner = () => (
    <div className="spinner-border text-primary" role="status" style={{ width: "1.5rem", height: "1.5rem" }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  );


  if (roleLoading) {
    return (
      <div className="container-fluid py-4" style={{ direction: "rtl" }}>
        <div className="text-center py-5">
          <LoadingSpinner />
          <p className="mt-3 text-muted">جاري تحميل بيانات الصلاحية...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ direction: "rtl" }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/roles" className="text-decoration-none mx-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: "8px" }}
              >
                <FaArrowLeft className="mx-2" />
                العودة
              </motion.button>
            </Link>
            <h4 className="mb-0 fw-bold" style={{ color: "var(--main-color)", fontSize: "1.1rem" }}>
              <FaShieldAlt className="mx-2" />
              تعديل الصلاحية: {originalRole?.name || "غير محدد"}
            </h4>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: "15px" }}
          >
            <div className="card-header bg-white border-0 py-3">
              <h4 className="text-center mb-0 fw-bold" style={{ color: "var(--main-color)", fontSize: "1.3rem" }}>
                تعديل بيانات الصلاحية
              </h4>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                {/* Role Name */}
                <div className="mb-4">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, textAlign: "right" }}>اسم الصلاحية *</label>
                  <input
                    type="text"
                    name="name"
                    value={roleData.name}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 9,
                      border: "1.5px solid #e2e8f0",
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                      backgroundColor: "#fff",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--main-color)";
                      e.target.style.boxShadow = "0 0 0 3px rgba(36, 18, 58, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "none";
                    }}
                    placeholder="أدخل اسم الصلاحية..."
                    required
                  />
                </div>

                {/* Permissions */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="form-label fw-medium mb-0">الصلاحيات المتاحة</label>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleSelectAll}
                      style={{ borderRadius: "8px" }}
                    >
                      {roleData.permission_id.length === permissions.length
                        ? "إلغاء الكل"
                        : "اختيار الكل"}
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <LoadingSpinner />
                      <p className="mt-3 text-muted">جاري تحميل الصلاحيات...</p>
                    </div>
                  ) : (
                    <div className="row">
                      {permissions.map((permission) => {
                        const isChecked = roleData.permission_id.some(
                          (p) => (typeof p === "object" ? p.id : p) === permission.id
                        );
                        return (
                          <div key={permission.id} className="col-md-6 col-lg-4 mb-3">
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "10px 14px",
                                border: isChecked ? "1.5px solid var(--main-color)" : "1.5px solid #e2e8f0",
                                background: isChecked ? "rgba(36, 18, 58, 0.03)" : "#fff",
                                borderRadius: 10,
                                cursor: "pointer",
                                userSelect: "none",
                                fontSize: 13,
                                color: isChecked ? "var(--main-color)" : "#334155",
                                fontWeight: isChecked ? 700 : 500,
                                transition: "all 0.2s"
                              }}
                            >
                              <input
                                type="checkbox"
                                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--main-color)" }}
                                checked={isChecked}
                                onChange={() => handlePermissionChange(permission)}
                              />
                              <span>{translatePermission(permission.name)}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Pagination meta={permissionsPagination} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
                </div>

                {/* Selected Count */}
                {roleData.permission_id.length > 0 && (
                  <div className="alert alert-info mb-4" style={{ borderRadius: 10, border: "none", background: "#f0f4ff", color: "#1e1b4b" }}>
                    <strong>
                      ℹ️ تم اختيار {roleData.permission_id.length} صلاحية
                    </strong>
                  </div>
                )}

                {/* Submit */}
                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="btn text-white px-5 py-2 fw-medium"
                    style={{
                      background: "linear-gradient(135deg,var(--main-color),var(--main-light))",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "1rem",
                      fontWeight: 700
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <FaSave className="mx-2" style={{ marginLeft: 6 }} />
                        حفظ التغييرات
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EditRole;
